<script setup>
import { useQuery } from '@vue/apollo-composable';
import axios from 'axios';
import { BAccordion, BAccordionItem, BButton, BOverlay } from 'bootstrap-vue-next';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import ErrorState from '~/common/components/ErrorState.vue';
import HelpAction from '~/common/components/HelpAction.vue';
import LabelAction from '~/common/components/labels/LabelAction.vue';
import { gidToId, idToGid } from '~/common/GidUtils';
import { useVueData } from '~/common/useVueData';
import { MergeRequestDetailsFromIdQueryDocument, MergeRequestDetailsQueryDocument } from '~/graphql/types';

import RequestReviewAction from './manage-merge-request-page/RequestReviewAction.vue';

const route = useRoute();
const { action } = route.query;
const { username } = useVueData();

const mergeRequestQuery = route.query.projectId ? MergeRequestDetailsQueryDocument : MergeRequestDetailsFromIdQueryDocument;
const { result: stagedResult } = useQuery(mergeRequestQuery, {
  mergeRequestId: idToGid('MergeRequest', route.query.mergeRequestId),
  mergeRequestIid: route.query.mergeRequestIid,
  projectId: idToGid('Project', route.query.projectId),
});

const result = computed(() => route.query.projectId ? stagedResult.value?.projects.nodes[0] : stagedResult.value);
const mergeRequest = computed(() => result.value?.mergeRequest);
const mergeRequestIid = computed(() => mergeRequest.value?.iid?.toString());
const projectId = computed(() => gidToId(mergeRequest.value?.project?.id)?.toString());
const reviewerNodes = computed(() => mergeRequest.value?.reviewers.nodes || []);
const reviewers = computed(() => reviewerNodes.value.map(reviewer => reviewer.username));
const assignees = computed(() => mergeRequest.value?.assignees.nodes?.map(assignee => assignee.username) || []);
const author = computed(() => mergeRequest.value?.author.username);
const participants = computed(() => [author.value, ...reviewers.value, ...assignees.value]);
const authorized = computed(() => data.value?.authorized || participants.value?.includes(username));
const otherReviewers = computed(() => reviewers.value?.filter(reviewer => !['gitlabduo', username.toLowerCase()].includes(reviewer.toLowerCase())));
const duoHasReviewed = computed(() =>
  reviewerNodes.value.some(reviewer =>
    reviewer.username.toLowerCase() === 'gitlabduo' && reviewer.mergeRequestInteraction?.reviewed,
  ),
);
const groupId = computed(() => gidToId(mergeRequest.value?.project?.group?.id)?.toString());
const loading = computed(() => !mergeRequest.value || (!data.value && !error.value));

const data = ref(null);
const error = ref(false);
const loadLabelAction = ref(false);

let fetchStarted = false;
const fetchData = async () => {
  fetchStarted = true;
  try {
    const response = await axios.get('/api/v1/manage_merge_request', {
      params: { project_web_url: mergeRequest.value.project.webUrl },
    });
    data.value = response.data;
  } catch {
    error.value = true;
  }
};

onMounted(() => {
  watch(mergeRequest, () => {
    if (!mergeRequest.value || fetchStarted) {
      return;
    }

    fetchData();
  }, { immediate: true });
});
</script>

<template>
  <BOverlay
    v-if="loading"
    :show="true"
    no-wrap
    fixed
  />
  <template v-else>
    <h1>Manage Merge Request</h1>
    <div>
      <a :href="mergeRequest.project.webUrl">{{ mergeRequest.project.name }}</a>:
      <a :href="mergeRequest.webUrl">{{ mergeRequest.title }}</a>
      <br>
      <div>
        Author: {{ author }}
      </div>
      <div>
        Reviewers: {{ reviewers.join(', ') }}
      </div>
      <div>
        Assignees: {{ assignees.join(', ') }}
      </div>
      <br>
      <ErrorState v-if="error" />
      <div v-else-if="mergeRequest.state !== 'opened'">
        You can only manage merge requests that are open.
      </div>
      <template v-else-if="authorized">
        <BAccordion class="mb-2">
          <BAccordionItem
            title="Request review"
            :visible="!action || action == 'review'"
          >
            <RequestReviewAction
              :duo-has-reviewed="duoHasReviewed"
              :merge-request-iid="mergeRequestIid"
              :other-reviewers="otherReviewers"
              :project-id="projectId"
              :suggested-reviewer="data.suggestedReviewer"
              :web-url="mergeRequest.webUrl"
              @error="error = true"
            />
          </BAccordionItem>
        </BAccordion>
        <BAccordion
          class="mb-2"
          @update:model-value="loadLabelAction = true"
        >
          <BAccordionItem
            title="Label"
            :visible="action == 'label'"
          >
            <LabelAction
              v-if="loadLabelAction"
              :description="mergeRequest.description"
              :group-id="groupId"
              :groups="data.groups"
              :iid="mergeRequestIid"
              :is-issue="false"
              :labels="mergeRequest.labels.nodes.map(label => label.title)"
              :label-config="data.labels"
              :project-id="projectId"
              :title="mergeRequest.title"
              :web-url="mergeRequest.webUrl"
              @error="error = true"
            />
          </BAccordionItem>
        </BAccordion>
        <BAccordion>
          <BAccordionItem
            title="Help"
            :visible="!action || action == 'help'"
          >
            <HelpAction
              :iid="mergeRequestIid"
              :reviewers="otherReviewers"
              :project-id="projectId"
              :suggested-reviewer="data.suggestedReviewer"
              :web-url="mergeRequest.webUrl"
              @error="error = true"
            />
          </BAccordionItem>
        </BAccordion>
      </template>
      <div v-else>
        To manage merge requests you must be:
        <ul>
          <li>The author</li>
          <li>An assignee</li>
          <li>A reviewer</li>
          <li>An onboarded community member</li>
          <li>A GitLab team member</li>
        </ul>
        <BButton
          variant="primary"
          :to="{
            name: 'home',
            hash: '#start',
            query: { referrer: encodeURIComponent(mergeRequest?.webUrl) } }
          "
        >
          Start onboarding
        </BButton>
      </div>
    </div>
  </template>
</template>
